import { useState, useEffect, Fragment } from 'react';
import { useRouter } from 'next/router';
// @mui
import {
  Stack,
  Button,
  Typography,
  Card,
  CardContent,
  Box,
} from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
// components
import Iconify from '../../components/Iconify';
import SvgIconStyle from '../../components/SvgIconStyle';
// hooks
import useResponsive from '../../hooks/useResponsive';
// utils
import { fKrSendKST, fKrDeadlineDateHourKST } from '../../utils/formatTime';
import {
  MessageDialog,
  MessagePopover,
} from './';
import {
  MeetingAgainDialog,
  ExtendDeadlineDialog,
} from './nego';

const BlurText = styled('span')(({ theme }) => ({
  color: theme.palette.text.disabled,
}));

const BlueText = styled('span')(({ theme }) => ({
  color: theme.palette.info.main,
}));

// ----------

export default function BeforeNegotiationCard({ offer, openRejectDialog, reloadOffer, proceed }) {
  const smUp = useResponsive('up', 'sm');
  const [openMeetingAgainDialog, setOpenMeetingAgainDialog] = useState(false);
  const [showCoffeeChatTask, setShowCoffeeChatTask] = useState(false);
  const [openExtendDeadlineDialog, setOpenExtendDeadlineDialog] = useState(false);
  const router = useRouter();
  const { extend } = router.query;

  useEffect(() => {
    if (extend === "y") {
      if (offer?.crew_nego_deadline && !offer?.crew_deadline_extend_at) {
        setOpenExtendDeadlineDialog(true);
      }
    }
  }, [extend]);

  return (
    <Fragment>
    <Stack sx={{ pt: !!offer?.instant_matched_at ? 0 : 3, mb: 3 }} spacing={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        {offer?.coffee_chat ? (
          <Typography variant="h4">
            ☕ 커피챗 한 회사와<br/><BlueText>파트타임 계약을 결정</BlueText>해주세요.
          </Typography>
        ) : (
          <Typography variant="h4">
            {offer?.offer_status === "company-negotiate-rewrite" ? "회사에서 계약 조건 수정을 요청하였습니다." : "사전 미팅은 잘 마치셨나요? 계약을 결정해 주세요."}
          </Typography>
        )}
        {smUp && (
          <Box>
            <MessagePopover companyId={offer?.company_id} companyName={offer?.company_name} />
          </Box>
        )}
      </Stack>
      {!smUp && (
        <Box>
          <MessageDialog companyId={offer?.company_id} companyName={offer?.company_name} />
        </Box>
      )}
      {offer?.crew_nego_deadline && offer?.crew_deadline_extend_at && (
        <Box
          sx={{
            px: 2.5,
            py: 2,
            borderRadius: 1.5,
            bgcolor: (theme) => alpha(theme.palette.error.main, 0.08),
          }}
        >
          <Typography variant="body2">
            <b>{fKrDeadlineDateHourKST(offer?.crew_nego_deadline)}</b>까지 계약 조건을 작성하지 않으면 매칭이 중단됩니다.
          </Typography>
        </Box>
      )}
      <Card>
        <CardContent>
          <Stack spacing={3}>
            {/* company name */}
            <Stack direction="row" spacing={1} alignItems="center">
              <SvgIconStyle src={`/icons/icon-building-mono.svg`} sx={{ color: "text.disabled" }} />
              <Typography variant="subtitle1">
                {offer?.company_name}
              </Typography>
            </Stack>
            {/* meeting time */}
            <Stack direction="row" spacing={1} alignItems="center">
              <SvgIconStyle src={`/icons/icon-clock-mono.svg`} sx={{ color: "text.disabled" }} />
              {offer?.coffee_chat ? (
                <Typography variant="subtitle1">
                  {fKrSendKST(offer?.coffee_chat_time)} <BlurText>커피챗 진행</BlurText>
                </Typography>
              ):(
              <Typography variant="subtitle1">
                {fKrSendKST(offer?.meeting_3way_time)} <BlurText>미팅 진행</BlurText>
              </Typography>
              )}
            </Stack>
            {offer?.coffee_chat ? (
            <Stack spacing={1}>
              <Stack direction="row" spacing={1} alignItems="center">
                <SvgIconStyle src={`/icons/icon-clock-mono.svg`} sx={{ color: "text.disabled" }} />
                <Typography variant="h6">
                  요청한 업무 상세
                </Typography>
              </Stack>
              <Box sx={{ height: showCoffeeChatTask ? "100%" : 75, overflowY: "clip" }}>
                <Typography sx={{ pl: 4, whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
                  {offer?.task_for_4weeks}
                </Typography>
                {!showCoffeeChatTask && (
                <Box
                  sx={{
                    width: 1,
                    position: "absolute",
                    bottom: 16,
                    backgroundColor: "rgba(255, 255, 255, 0.7)",
                    backdropFilter: "blur(2px)",
                  }}
                  wrapperClassName="wrapper"
                >
                  {/* TOOD: 버튼 줄이고 늘리고 */}
                  <Button color="info" sx={{ ml: 3 }}
                    endIcon={<Iconify icon="eva:arrow-ios-downward-fill" />}
                    onClick={() => setShowCoffeeChatTask(true)}
                  >
                    내용 전체보기
                  </Button>
                </Box>
                )}
              </Box>
            </Stack>
            ):(
            <Box>
              <Button
                disableFocusRipple
                variant="text"
                size="large"
                color="info"
                startIcon={<RoundIcon color="info" svgIconPath="/icons/ic_videocam.svg" />}
                endIcon={<Iconify icon="ic:baseline-chevron-right" />}
                onClick={() => setOpenMeetingAgainDialog(true)}
              >
                미팅을 한번 더 하기로 했나요?
              </Button>
            </Box>
            )}
          </Stack>
        </CardContent>
      </Card>
      {offer?.crew_nego_deadline && !offer?.crew_deadline_extend_at && (
        <Box
          sx={{
            p: 3,
            borderRadius: 2,
            bgcolor: "background.neutral",
          }}
        >
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <Iconify icon="eva:alert-circle-fill" sx={{ color: "error.main", minWidth: 24, minHeight: 24 }} />
            <Stack>
              <Typography variant="subtitle1" sx={{ color: "error.main", pb: 0.5 }}>
                기업과 협의가 필요해서 시간이 더 필요하다면, 기한을 연장해 주세요.
              </Typography>
              <Typography variant="body2">
                <b>{fKrDeadlineDateHourKST(offer?.crew_nego_deadline)}</b>까지 계약 조건을 작성하지 않으면 매칭이 중단됩니다.<br/>
                기한은 최대 7일 연장할 수 있으며, 한번만 가능합니다.
              </Typography>
            </Stack>
          </Stack>
          <Stack direction="row" justifyContent="flex-end">
            <Button
              disableFocusRipple
              variant="text"
              color="error"
              onClick={() => setOpenExtendDeadlineDialog(true)}
              endIcon={<Iconify icon="ic:baseline-chevron-right" />}
              sx={{ maxWidth: "fit-content" }}
            >
              결정 기한 연장하기
            </Button>
          </Stack>
        </Box>
      )}
      <Stack direction={smUp ? "row" : "column-reverse"} sx={{ pt: 1, width: 1 }} justifyContent="flex-end" spacing={2}>
        <Button
          variant="outlined"
          size="medium"
          sx={{ px: 4 }}
          onClick={openRejectDialog}
        >
          계약하지 않겠습니다
        </Button>
        <Button
          variant="contained"
          size="medium"
          sx={{ px: 4 }}
          onClick={proceed}
        >
          계약 진행하기
        </Button>
      </Stack>

      {/* Spacing */}
      <Box />

    </Stack>
    <MeetingAgainDialog
      meetingCount={offer?.meeting_3way_count}
      isOpen={openMeetingAgainDialog}
      closeDialog={() => setOpenMeetingAgainDialog(false)}
      reloadOffer={reloadOffer}
    />
    <ExtendDeadlineDialog
      currentDeadline={offer?.crew_nego_deadline}
      isOpen={openExtendDeadlineDialog}
      closeDialog={() => setOpenExtendDeadlineDialog(false)}
      reloadOffer={reloadOffer}
    />
    </Fragment>
  )
};


function RoundIcon({ color, svgIconPath }) {
  return (
    <Box
      sx={{
        width: 32,
        height: 32,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '50%',
        backgroundColor: (theme) => alpha(theme.palette[color].main, 0.08)
      }}
    >
      <SvgIconStyle
        src={svgIconPath}
        sx={{
          width: 20,
          height: 20,
          color: `${color}.main`,
        }}
      />
    </Box>
  );
}
